// TanStack Query hooks for meetings data
import { useMemo, useSyncExternalStore } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { meetingsService } from '../../services/meetingsService';
import type { Meeting } from '../../types/meetings';
import { useCalendarNameMap } from './useCalendarNameMap';

// Query key factory
export const meetingsQueryKeys = {
  all: ['meetings'] as const,
  calendarDay: (calendarEmail: string, date: string) => {
    return [...meetingsQueryKeys.all, calendarEmail, 'day', date] as const;
  },
  outOfSyncMeetings: (hostEmail?: string, startDate?: string, endDate?: string) => {
    return [...meetingsQueryKeys.all, 'out-of-sync', hostEmail, startDate, endDate] as const;
  },
  findRoomReplacements: (nodeId?: string, bookingId?: string, calendar_owner_email?: string) => {
    return [...meetingsQueryKeys.all, 'find-room-replacements', nodeId, bookingId, calendar_owner_email] as const;
  },
  calendarRange: (calendarEmail: string, startDate: string, endDate: string) => {
    return [...meetingsQueryKeys.all, calendarEmail, 'range', startDate, endDate] as const;
  },
};

// Helper to get all dates in a range
const getDateRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  const current = new Date(start);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

// Helper to group consecutive dates into ranges
interface DateRange {
  start: string;
  end: string;
}

const groupConsecutiveDates = (dates: string[]): DateRange[] => {
  if (dates.length === 0) return [];

  const sorted = [...dates].sort();
  const ranges: DateRange[] = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const current = new Date(sorted[i]);
    const previous = new Date(sorted[i - 1]);
    const diffDays = Math.round((current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      rangeEnd = sorted[i];
    } else {
      // Gap found, save current range and start new one
      ranges.push({ start: rangeStart, end: rangeEnd });
      rangeStart = sorted[i];
      rangeEnd = sorted[i];
    }
  }

  ranges.push({ start: rangeStart, end: rangeEnd });
  return ranges;
};

interface UseMeetingsParams {
  calendars: string[];
  startDate: string; // ISO format (YYYY-MM-DD or date-only ISO string)
  endDate: string; // ISO format (YYYY-MM-DD or date-only ISO string)
  enabled?: boolean;
}

/**
 * Hook to fetch meetings with intelligent caching and batching
 * - Checks cache for each day
 * - Groups consecutive missing days into ranges
 * - Makes one API call per missing range per calendar
 * - Populates day-based cache for future lookups
 */
export const useMeetings = ({ calendars, startDate, endDate, enabled = true }: UseMeetingsParams) => {
  const queryClient = useQueryClient();
  const { calendarNameMap } = useCalendarNameMap();

  // Stabilize calendars to prevent unnecessary re-fetches
  const stableCalendars = useMemo(() => [...calendars].sort(), [calendars.join(',')]);

  const datesInRange = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  const queries = useMemo(() => {
    const queryList: Array<{
      queryKey: readonly unknown[];
      queryFn: () => Promise<any>;
      enabled: boolean;
      staleTime: number;
      gcTime: number;
      refetchOnWindowFocus: boolean;
      refetchOnMount: boolean;
    }> = [];

    let totalCachedDays = 0;
    let totalFetchRanges = 0;

    const calendarQueryMap = new Map<string, number[]>();

    for (const calendarEmail of stableCalendars) {
      const queryIndices: number[] = [];

      const missingDates: string[] = [];

      for (const date of datesInRange) {
        const dayKey = meetingsQueryKeys.calendarDay(calendarEmail, date);
        const cachedData = queryClient.getQueryData(dayKey);
        const queryState = queryClient.getQueryState(dayKey);
        const isFresh =
          queryState && queryState.dataUpdatedAt > 0 && Date.now() - queryState.dataUpdatedAt < 1000 * 60 * 5;

        if (!cachedData || !isFresh) {
          missingDates.push(date);
        } else {
          totalCachedDays++;
        }
      }

      const shouldFetchWholeRange =
        missingDates.length > datesInRange.length * 0.5 || groupConsecutiveDates(missingDates).length > 1;

      let rangesToFetch: DateRange[];
      if (shouldFetchWholeRange && missingDates.length > 0) {
        rangesToFetch = [{ start: datesInRange[0], end: datesInRange[datesInRange.length - 1] }];
      } else {
        rangesToFetch = groupConsecutiveDates(missingDates);
      }

      totalFetchRanges += rangesToFetch.length;

      for (const range of rangesToFetch) {
        const queryIndex = queryList.length;
        queryIndices.push(queryIndex);

        queryList.push({
          queryKey: meetingsQueryKeys.calendarRange(calendarEmail, range.start, range.end),
          queryFn: async () => {
            const result = await meetingsService.getMeetings(
              {
                calendar_emails: [calendarEmail],
                start_date: range.start,
                end_date: range.end,
              },
              calendarNameMap,
            );

            const rangeDates = getDateRange(range.start, range.end);
            for (const date of rangeDates) {
              const dayKey = meetingsQueryKeys.calendarDay(calendarEmail, date);
              const dayMeetings = result.meetings.filter((m: Meeting) => {
                const start = m.startDate instanceof Date ? m.startDate : new Date(m.startDate);
                const end = m.endDate instanceof Date ? m.endDate : new Date(m.endDate);
                const startDate_str = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;

                // If meeting ends at midnight, it doesn't span into that day
                const endAtMidnight = end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0;
                const effectiveEnd = endAtMidnight ? new Date(end.getTime() - 1) : end;
                const endDate_str = `${effectiveEnd.getFullYear()}-${String(effectiveEnd.getMonth() + 1).padStart(2, '0')}-${String(effectiveEnd.getDate()).padStart(2, '0')}`;

                return date >= startDate_str && date <= endDate_str;
              });

              queryClient.setQueryData(dayKey, {
                meetings: dayMeetings,
                freeSlots: result.freeSlots.filter((slot: any) => {
                  const slotDateObj = slot.start instanceof Date ? slot.start : new Date(slot.start);
                  const sy = slotDateObj.getFullYear();
                  const smo = String(slotDateObj.getMonth() + 1).padStart(2, '0');
                  const sd = String(slotDateObj.getDate()).padStart(2, '0');
                  const slotDate = `${sy}-${smo}-${sd}`;
                  return slotDate === date;
                }),
                totalMeetings: dayMeetings.length,
              });
            }

            return result;
          },
          enabled: enabled && stableCalendars.length > 0,
          staleTime: 1000 * 60 * 5,
          gcTime: 1000 * 60 * 5,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
        });
      }

      calendarQueryMap.set(calendarEmail, queryIndices);
    }

    return { queryList, calendarQueryMap };
  }, [stableCalendars, datesInRange, enabled, queryClient, calendarNameMap]);

  const rangeQueries = useQueries({ queries: queries.queryList });

  const queryCache = queryClient.getQueryCache();
  const cacheVersion = useSyncExternalStore(
    (onStoreChange) => queryCache.subscribe(onStoreChange),
    () =>
      queryCache.getAll().reduce((acc, query) => {
        if (query.queryKey[0] === 'meetings') {
          return acc + (query.state.dataUpdatedAt || 0);
        }
        return acc;
      }, 0),
  );

  // Extract data from range queries to make combinedData reactive to cache updates
  const rangeQueriesData = useMemo(() => rangeQueries.map((q: any) => q.data), [rangeQueries]);

  const combinedData = useMemo(() => {
    const meetingsMap = new Map<string, Meeting>();
    const allFreeSlots: any[] = [];
    const seenSlots = new Set<string>();
    let hasAnyData = false;

    for (const calendarEmail of stableCalendars) {
      for (const date of datesInRange) {
        const dayKey = meetingsQueryKeys.calendarDay(calendarEmail, date);
        const dayData = queryClient.getQueryData<{ meetings: Meeting[]; freeSlots: any[]; totalMeetings: number }>(
          dayKey,
        );

        if (dayData) {
          hasAnyData = true;
          for (const meeting of dayData.meetings) {
            if (!meetingsMap.has(meeting.id)) {
              meetingsMap.set(meeting.id, meeting);
            }
          }
          for (const slot of dayData.freeSlots) {
            const slotKey = `${slot.start}-${slot.end}-${slot.calendarEmail ?? calendarEmail}`;
            if (!seenSlots.has(slotKey)) {
              seenSlots.add(slotKey);
              allFreeSlots.push(slot);
            }
          }
        }
      }
    }

    if (!hasAnyData && rangeQueries.some((q: any) => q.isLoading)) {
      return undefined;
    }

    const allMeetings = Array.from(meetingsMap.values());

    return {
      calendars,
      startDate,
      endDate,
      meetings: allMeetings,
      freeSlots: allFreeSlots,
      totalMeetings: allMeetings.length,
    };
  }, [
    cacheVersion,
    rangeQueries,
    rangeQueriesData,
    stableCalendars,
    datesInRange,
    queryClient,
    calendars,
    startDate,
    endDate,
  ]);

  const isLoading = rangeQueries.some((q: any) => q.isLoading) && !combinedData;

  // Track loading state per calendar
  const loadingStates = useMemo(() => {
    const states = new Map<string, boolean>();

    for (const calendarEmail of stableCalendars) {
      const queryIndices = queries.calendarQueryMap.get(calendarEmail) || [];
      const calendarLoading = queryIndices.some((index: number) => rangeQueries[index]?.isLoading);
      states.set(calendarEmail, calendarLoading);
    }

    return states;
  }, [rangeQueries, stableCalendars, queries.calendarQueryMap]);

  const errorStates = useMemo(() => {
    const states = new Map<string, Error | null>();

    for (const calendarEmail of stableCalendars) {
      const queryIndices = queries.calendarQueryMap.get(calendarEmail) || [];
      const calendarError =
        queryIndices
          .map((index: number) => rangeQueries[index]?.error as Error | null)
          .find((error: Error | null) => error !== null) || null;
      states.set(calendarEmail, calendarError);
    }

    return states;
  }, [rangeQueries, stableCalendars, queries.calendarQueryMap]);

  return {
    data: combinedData,
    isLoading,
    dayQueries: rangeQueries,
    loadingStates,
    errorStates,
  };
};

// Convenience export for combined data access
export const useMeetingsData = (params: UseMeetingsParams) => {
  const query = useMeetings(params);

  return {
    // Raw TanStack object
    meetingsQuery: query,

    // Convenience data access (now using domain model camelCase)
    meetings: query.data?.meetings ?? [],
    freeSlots: query.data?.freeSlots ?? [],
    totalMeetings: query.data?.totalMeetings ?? 0,
    calendars: query.data?.calendars,
    isLoading: query.isLoading,
    loadingStates: query.loadingStates,
    errorStates: query.errorStates,
  };
};
